import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config/api';
import { toast } from 'react-toastify';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Validation schema
const UserSchema = Yup.object().shape({
  departmentName: Yup.string()
    .required('Department name is required')
    .min(2, 'Department name must be at least 2 characters'),
  departmentHead: Yup.string()
    .required('Department Head is required'),
});

const AdminDepartmentMaster = () => {
  const { getAuthHeaders } = useAuth();
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // Initial form values for new user
  const initialValues = {
    departmentName: '',
    departmentHead:'',
  };

  // Fetch all departments
  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/departments`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch departments');
      }
      
      const data = await response.json();
      setUsers(data);
    } catch (error) {
      setError('Error fetching departments: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all users for department head dropdown
  const fetchAllUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/users`, {
        headers: getAuthHeaders(),
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch users');
      }
      
      const data = await response.json();
      setAllUsers(data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  useEffect(() => {
    fetchDepartments();
    fetchAllUsers();
  }, []);

  // Reset form
  const resetForm = () => {
    setEditingUser(null);
  };

  // Open modal for adding new user
  const handleAddUser = () => {
    setEditingUser(null); // Clear editing user to ensure clean form
    setOpenModal(true);
  };

  // Open modal for editing user
  const handleEditUser = (user) => {
    setEditingUser(user);
    setOpenModal(true);
  };



  // Handle form submission (create or update)
  const handleSubmit = async (values, { setSubmitting, resetForm: resetFormik }) => {
    await performSubmit(values, { setSubmitting, resetForm: resetFormik });
  };

  // Actual submission logic
  const performSubmit = async (values, { setSubmitting, resetForm: resetFormik }) => {
    try {
      const url = editingUser 
        ? `${API_URL}/departments/${editingUser._id}`
        : `${API_URL}/departments`;
      
      const method = editingUser ? 'PUT' : 'POST';
      
      // For departments, we only need departmentName and departmentHead
      const body = {
        departmentName: values.departmentName,
        departmentHead: values.departmentHead
      };

      const response = await fetch(url, {
        method,
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Operation failed');
      }
      
      const resultData = await response.json();
      
      if (!editingUser) {
        toast.success('Department created successfully!');
      } else {
        toast.success('Department updated successfully!');
      }

      setOpenModal(false);
      resetForm();
      resetFormik();
      fetchDepartments(); // Refresh the list
    } catch (error) {
      setError('Error: ' + error.message);
      toast.error('Operation failed: ' + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle department deletion
  const handleDeleteDepartment = async (departmentId) => {
    if (!window.confirm('Are you sure you want to delete this department?')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/departments/${departmentId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete department');
      }

      toast.success('Department deleted successfully!');
      fetchDepartments(); // Refresh the list
    } catch (error) {
      setError('Error deleting department: ' + error.message);
      toast.error('Failed to delete department: ' + error.message);
    }
  };


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px' ,lineHeight:1.5 ,letterSpacing:1}}>
          Department Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddUser}
          sx={{ textTransform: 'none' }}
        >
          Add New Department
        </Button>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          action={
            <IconButton
              color="inherit"
              size="small"
              onClick={() => setError('')}
            >
              <CloseIcon />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Departments Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Department Name</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Department Head</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map(department => (
              <TableRow key={department._id} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                <TableCell>{department.departmentName}</TableCell>
                <TableCell>
                  {department.departmentHead ? 
                    `${department.departmentHead.firstName} ${department.departmentHead.lastName}` : 
                    'Not assigned'
                  }
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleEditUser(department)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDeleteDepartment(department._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal for Add/Edit User */}
      <Dialog 
        open={openModal} 
        onClose={() => {
          setOpenModal(false);
          resetForm();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {editingUser ? 'Edit Department' : 'Add New Department'}
          </Typography>
          <IconButton
            onClick={() => {
              setOpenModal(false);
              resetForm();
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        
        <Formik
          key={editingUser ? editingUser._id : 'new-user'} // Force re-render when switching between add/edit
          enableReinitialize={true}
          initialValues={editingUser ? {
            departmentName: editingUser.departmentName || '',
            departmentHead: editingUser.departmentHead?._id || editingUser.departmentHead || '',
          } : initialValues}
          validationSchema={UserSchema}
          context={{ isEditing: !!editingUser }}
          validateOnMount={false}
          validateOnChange={true}
          validateOnBlur={true}
          onSubmit={handleSubmit}
        >
                     {({ values, errors, touched, handleChange, handleBlur, setFieldValue, isSubmitting, validateForm }) => (
            <Form>
              <DialogContent>
                <Grid container spacing={2}>
                  <Grid item xs={12} >
                    <Field
                      as={TextField}
                      fullWidth
                      label="Department Name"
                      name="departmentName"
                      value={values.departmentName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={touched.departmentName && Boolean(errors.departmentName)}
                      helperText={touched.departmentName && errors.departmentName}
                      margin="normal"
                    />
                  </Grid>
                  
                  <Grid item xs={12} >
                    <Autocomplete
                      fullWidth
                      margin="normal"
                      options={allUsers}
                      value={allUsers.find(u => (u._id || u.id) === values.departmentHead) || null}
                      onChange={(event, newValue) => {
                        setFieldValue('departmentHead', newValue ? (newValue._id || newValue.id) : '');
                      }}
                      onBlur={handleBlur}
                      getOptionLabel={(option) => `${option.firstName || 'N/A'} ${option.lastName || 'N/A'} - ${option.email || 'N/A'}`}
                      loading={loading}
                      disabled={loading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Department Head"
                          placeholder="Search employee by name or email..."
                          error={touched.departmentHead && Boolean(errors.departmentHead)}
                          helperText={touched.departmentHead && errors.departmentHead}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loading ? <CircularProgress size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      renderOption={(props, user) => (
                        <Box component="li" {...props} key={user._id || user.id}>
                          <Typography variant="body1">
                            {user.firstName || 'N/A'} {user.lastName || 'N/A'} - {user.email || 'N/A'}
                          </Typography>
                        </Box>
                      )}
                      filterOptions={(options, { inputValue }) => {
                        return options.filter((option) => {
                          const searchTerm = inputValue.toLowerCase();
                          const firstName = (option.firstName || '').toLowerCase();
                          const lastName = (option.lastName || '').toLowerCase();
                          const email = (option.email || '').toLowerCase();
                          const fullName = `${firstName} ${lastName}`.trim();
                          return fullName.includes(searchTerm) || email.includes(searchTerm);
                        });
                      }}
                      noOptionsText={loading ? 'Loading users...' : 'No employees found'}
                    />
                  </Grid>
                </Grid>
               </DialogContent>
               
               <DialogActions sx={{ p: 3 }}>
                 <Button
                   onClick={() => {
                     setOpenModal(false);
                     resetForm();
                   }}
                   sx={{ textTransform: 'none' }}
                   disabled={isSubmitting}
                 >
                   Cancel
                 </Button>
                 <Button
                   type="submit"
                   variant="contained"
                   sx={{ textTransform: 'none' }}
                   disabled={isSubmitting}
                 >
                   {isSubmitting ? 'Saving...' : (editingUser ? 'Update Department' : 'Create Department')}
                 </Button>
               </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </Box>
  );
};

export default AdminDepartmentMaster; 